import { useCallback } from 'react'
import { Link } from 'react-router-dom'
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion'
import {
  ArrowRight,
  Code,
  DollarSign,
  LayoutGrid,
  LogOut,
  Rocket,
  ShieldCheck,
  TrendingUp,
  User,
  Users,
  Zap,
  Terminal,
} from 'lucide-react'
import { PublicLayout } from '@/components/PublicLayout'
import { LandingButton } from '@/components/LandingButton'
import { useAuth } from '@/lib/auth'

const EASE_STANDARD: [number, number, number, number] = [0.25, 0.46, 0.45, 0.94]

const ILLUSTRATIONS = {
  hiring: '/illustrations/hiring.svg',
  working: '/illustrations/working.svg',
  businessDeal: '/illustrations/business-deal.svg',
  agreement: '/illustrations/agreement.svg',
}

// ---------------------------------------------------------------------------
// Hero Section
// ---------------------------------------------------------------------------

function HeroSection() {
  const prefersReducedMotion = useReducedMotion()

  const fadeUp = useCallback(
    (delay: number, yStart = 40) => ({
      initial: prefersReducedMotion ? false : ({ opacity: 0, y: yStart } as const),
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.65, ease: EASE_STANDARD, delay },
    }),
    [prefersReducedMotion]
  )

  return (
    <section className="relative flex min-h-[88vh] items-center overflow-hidden px-8 pb-16 pt-20">
      {/* Gradient mesh background */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 20% 40%, rgba(91,146,121,0.07) 0%, transparent 70%), radial-gradient(ellipse 60% 50% at 80% 30%, rgba(232,152,90,0.06) 0%, transparent 70%)',
        }}
      />

      <div className="relative z-10 mx-auto grid w-full max-w-[1120px] grid-cols-1 items-center gap-12 lg:grid-cols-[1fr_1.1fr]">
        {/* Left: Text */}
        <div>
          {/* Developer badge */}
          <motion.div
            {...fadeUp(0, 20)}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-[13px] font-semibold text-muted-foreground shadow-[0_1px_3px_rgba(45,42,38,0.06)]"
          >
            <Terminal className="h-4 w-4 text-primary" />
            For Developers
          </motion.div>

          <motion.h1
            {...fadeUp(0)}
            className="mb-6 text-foreground"
            style={{
              fontFamily: 'Fraunces, Georgia, serif',
              fontSize: 'clamp(40px, 5vw, 56px)',
              fontWeight: 700,
              lineHeight: 1.08,
              letterSpacing: '-1.5px',
            }}
          >
            Build Your Reputation.
            <br />
            <span className="bg-gradient-to-br from-primary to-[#6BAE8A] bg-clip-text text-transparent">
              Get Paid Fairly.
            </span>
          </motion.h1>

          <motion.p
            {...fadeUp(0.12, 30)}
            className="mb-9 max-w-[460px] text-lg leading-[1.65] text-muted-foreground"
          >
            Stop chasing invoices and fighting scope creep. Masonyt connects you with vetted
            clients, protects your payments through escrow, and helps you build a verifiable career.
          </motion.p>

          <motion.div {...fadeUp(0.24, 24)}>
            <LandingButton to="/signup/developer">
              Join the Network
              <ArrowRight className="h-[18px] w-[18px] transition-transform group-hover:translate-x-[3px]" />
            </LandingButton>
          </motion.div>

          <motion.p
            initial={prefersReducedMotion ? false : { opacity: 0 }}
            animate={{ opacity: 0.8 }}
            transition={{ duration: 0.5, ease: 'easeOut', delay: 0.36 }}
            className="mt-3.5 text-[13px] text-muted-foreground"
          >
            Free to join &middot; Only vetted clients &middot; Escrow-protected payments
          </motion.p>
        </div>

        {/* Right: Illustration */}
        <motion.div
          className="relative flex items-center justify-center"
          initial={prefersReducedMotion ? false : { opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.75, ease: EASE_STANDARD, delay: 0.15 }}
        >
          <img
            src={ILLUSTRATIONS.hiring}
            alt="Developer being hired"
            className="w-full max-w-[520px] drop-shadow-[0_8px_32px_rgba(45,42,38,0.06)]"
            loading="eager"
          />
        </motion.div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Problem Section
// ---------------------------------------------------------------------------

const painCards = [
  {
    icon: TrendingUp,
    iconBg: 'bg-destructive/[0.08]',
    iconColor: 'text-destructive',
    title: 'Race to the Bottom',
    description:
      'Competing with low-quality bids on generic platforms forces you to undervalue your skills just to get noticed.',
    avatar: '\uD83D\uDE14',
    quote: '"I quoted fair. They picked the guy who said $5/hr."',
  },
  {
    icon: ShieldCheck,
    iconBg: 'bg-destructive/[0.08]',
    iconColor: 'text-destructive',
    title: 'Trust Issues',
    description:
      'Clients are risk-averse. Without a verified track record, landing the big, interesting projects feels impossible.',
    avatar: '\uD83E\uDD14',
    quote: '"They wanted 3 years of references for a 2-week project."',
  },
  {
    icon: DollarSign,
    iconBg: 'bg-destructive/[0.08]',
    iconColor: 'text-destructive',
    title: 'Chasing Payments',
    description:
      'Unclear contracts and scope creep lead to unpaid work, awkward conversations, and invoices that go unanswered.',
    avatar: '\uD83D\uDCB8',
    quote: '"Delivered the whole thing. Still waiting on payment 4 months later."',
  },
]

function ProblemSection() {
  const prefersReducedMotion = useReducedMotion()

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: EASE_STANDARD, delay: i * 0.12 },
    }),
  }

  return (
    <section className="bg-card px-8 py-[120px]">
      <div className="mx-auto max-w-[1120px]">
        <motion.div
          className="mb-4 text-xs font-bold uppercase tracking-[2.5px] text-accent"
          initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.65, ease: EASE_STANDARD }}
        >
          The Problem
        </motion.div>

        <motion.h2
          className="mb-5 max-w-[580px] text-foreground"
          style={{
            fontFamily: 'Fraunces, Georgia, serif',
            fontSize: '42px',
            fontWeight: 700,
            lineHeight: 1.15,
            letterSpacing: '-0.5px',
          }}
          initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.1 }}
        >
          The Freelance <em className="not-italic text-accent">Broken Record.</em>
        </motion.h2>

        <motion.p
          className="mb-14 max-w-[560px] text-[17px] leading-[1.65] text-muted-foreground"
          initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.2 }}
        >
          We know the drill. You write great code, but the system is stacked against independent
          developers. It&apos;s time to change the tune.
        </motion.p>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {painCards.map((card, i) => (
            <motion.div
              key={card.title}
              className="group rounded-[20px] border border-border bg-background p-7 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-accent hover:shadow-[0_12px_32px_rgba(45,42,38,0.08)]"
              variants={cardVariants}
              custom={i}
              initial={prefersReducedMotion ? false : 'hidden'}
              whileInView="visible"
              viewport={{ once: true, amount: 0.15 }}
            >
              <div
                className={`mb-5 flex h-12 w-12 items-center justify-center rounded-[14px] ${card.iconBg} transition-transform duration-300 group-hover:-rotate-3 group-hover:scale-110`}
              >
                <card.icon className={`h-6 w-6 ${card.iconColor}`} />
              </div>
              <h3 className="mb-2 text-lg font-bold text-foreground">{card.title}</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">{card.description}</p>
              <div className="mt-6 flex items-center gap-3 border-t border-border pt-5">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-muted text-sm">
                  {card.avatar}
                </div>
                <span className="text-xs italic leading-snug text-muted-foreground">
                  {card.quote}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Benefits / Trust Section — "The Masonyt Advantage"
// ---------------------------------------------------------------------------

const benefits = [
  {
    icon: ShieldCheck,
    title: 'Verified Track Record',
    description:
      'Every project you complete adds to your immutable history. Prove your reliability and skills with data, not just claims.',
  },
  {
    icon: DollarSign,
    title: 'Protected Payments',
    description:
      'Client funds are secured in escrow before you start. Payment release is tied to clear, pre-agreed milestones.',
  },
  {
    icon: Code,
    title: 'Agentic Frameworks',
    description:
      'Skip the boilerplate. Access our library of automated frameworks to speed up development and focus on complex logic.',
  },
  {
    icon: Zap,
    title: 'Zero Friction',
    description:
      'Standardized contracts and scopes mean less time negotiating and more time coding. We handle the admin overhead.',
  },
  {
    icon: Users,
    title: 'Vetted Clients',
    description:
      'We qualify clients to ensure they are serious and ready to build. No more tire-kickers or "equity only" offers.',
  },
  {
    icon: TrendingUp,
    title: 'Career Growth',
    description:
      'As you build your reputation, unlock access to larger, higher-paying projects and premium platform features.',
  },
]

function BenefitsSection() {
  const prefersReducedMotion = useReducedMotion()

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: EASE_STANDARD, delay: i * 0.12 },
    }),
  }

  return (
    <section className="bg-background px-8 py-[120px]">
      <div className="mx-auto max-w-[1120px]">
        {/* Header */}
        <div className="mb-[72px] text-center">
          <motion.div
            className="mb-4 text-xs font-bold uppercase tracking-[2.5px] text-primary"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.65, ease: EASE_STANDARD }}
          >
            Why Masonyt
          </motion.div>

          <motion.h2
            className="mb-3 text-foreground"
            style={{
              fontFamily: 'Fraunces, Georgia, serif',
              fontSize: '42px',
              fontWeight: 700,
              letterSpacing: '-0.5px',
            }}
            initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.1 }}
          >
            The Masonyt
            <br />
            <span className="text-primary">Advantage</span>
          </motion.h2>

          <motion.p
            className="mx-auto max-w-[480px] text-[17px] text-muted-foreground"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.2 }}
          >
            We provide the structure, you provide the code. It&apos;s that simple.
          </motion.p>
        </div>

        {/* 3x2 card grid */}
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {benefits.map((benefit, i) => (
            <motion.div
              key={benefit.title}
              className="group rounded-[20px] border border-border bg-card p-7 shadow-[0_4px_12px_rgba(45,42,38,0.08)] transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-primary hover:shadow-[0_12px_32px_rgba(45,42,38,0.06)]"
              variants={cardVariants}
              custom={i}
              initial={prefersReducedMotion ? false : 'hidden'}
              whileInView="visible"
              viewport={{ once: true, amount: 0.15 }}
            >
              <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/[0.08] transition-transform duration-300 group-hover:-rotate-3 group-hover:scale-110">
                <benefit.icon className="h-[26px] w-[26px] text-primary" />
              </div>
              <h3 className="mb-2.5 text-xl font-bold text-foreground">{benefit.title}</h3>
              <p className="text-sm leading-[1.65] text-muted-foreground">{benefit.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// How It Works Section
// ---------------------------------------------------------------------------

const steps = [
  {
    num: 1,
    title: 'Create Your Profile',
    description:
      'Showcase your stack, experience, and certifications. Get vetted to boost your visibility and unlock premium projects.',
    illustration: ILLUSTRATIONS.working,
    illustrationAlt: 'Developer creating profile',
  },
  {
    num: 2,
    title: 'Get Matched',
    description:
      'Receive project invites that match your skills, or browse the bounty board for public briefs. No more cold pitching.',
    illustration: ILLUSTRATIONS.hiring,
    illustrationAlt: 'Getting matched with projects',
  },
  {
    num: 3,
    title: 'Deliver & Earn',
    description:
      'Use our tools to manage the project through milestones. Upon delivery, payments are released instantly from escrow.',
    illustration: ILLUSTRATIONS.businessDeal,
    illustrationAlt: 'Completing a deal',
  },
]

function HowItWorksSection() {
  const prefersReducedMotion = useReducedMotion()

  return (
    <section id="how-it-works" className="relative bg-card px-8 py-[120px]">
      {/* Subtle gradient */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 50% 40% at 70% 20%, rgba(91,146,121,0.04) 0%, transparent 70%)',
        }}
      />

      <div className="relative mx-auto max-w-[1120px]">
        {/* Header */}
        <div className="mb-20 text-center">
          <motion.div
            className="mb-4 text-xs font-bold uppercase tracking-[2.5px] text-primary"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.65, ease: EASE_STANDARD }}
          >
            How It Works
          </motion.div>

          <motion.h2
            className="mb-3 text-foreground"
            style={{
              fontFamily: 'Fraunces, Georgia, serif',
              fontSize: '42px',
              fontWeight: 700,
              letterSpacing: '-0.5px',
            }}
            initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.1 }}
          >
            Focus on Code,
            <br />
            Not Sales
          </motion.h2>

          <motion.p
            className="mx-auto max-w-[500px] text-[17px] text-muted-foreground"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.2 }}
          >
            Three steps from sign-up to getting paid.
          </motion.p>
        </div>

        {/* Steps — alternating text + illustration */}
        <div className="flex flex-col gap-20">
          {steps.map((step, i) => {
            const isEven = i % 2 === 1
            const illustrationSlideX = isEven ? -40 : 40

            return (
              <div
                key={step.num}
                className={`grid grid-cols-1 items-center gap-16 lg:grid-cols-2 ${isEven ? 'lg:direction-rtl' : ''}`}
                style={isEven ? { direction: 'rtl' } : undefined}
              >
                {/* Text */}
                <div style={isEven ? { direction: 'ltr' } : undefined}>
                  <motion.div
                    className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-full border-[3px] border-primary bg-card text-primary shadow-[0_4px_12px_rgba(45,42,38,0.06)] transition-all duration-300 hover:scale-[1.08] hover:bg-primary hover:text-primary-foreground"
                    style={{
                      fontFamily: 'Fraunces, Georgia, serif',
                      fontSize: '18px',
                      fontWeight: 700,
                    }}
                    initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                  >
                    {step.num}
                  </motion.div>

                  <motion.h3
                    className="mb-2.5 text-[26px] font-bold text-foreground"
                    initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.65, ease: EASE_STANDARD }}
                  >
                    {step.title}
                  </motion.h3>

                  <motion.p
                    className="max-w-[420px] text-base leading-[1.65] text-muted-foreground"
                    initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.1 }}
                  >
                    {step.description}
                  </motion.p>
                </div>

                {/* Illustration */}
                <motion.div
                  className="flex items-center justify-center rounded-3xl border border-border bg-background p-6 transition-shadow duration-300 hover:shadow-[0_8px_32px_rgba(45,42,38,0.06)]"
                  style={isEven ? { direction: 'ltr' } : undefined}
                  initial={prefersReducedMotion ? false : { opacity: 0, x: illustrationSlideX }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.7, ease: EASE_STANDARD }}
                >
                  <img
                    src={step.illustration}
                    alt={step.illustrationAlt}
                    className="h-auto w-full max-w-[380px]"
                    loading="lazy"
                  />
                </motion.div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Terminal / Code Block Section — developer signature element
// ---------------------------------------------------------------------------

function TerminalSection() {
  const prefersReducedMotion = useReducedMotion()

  return (
    <section className="bg-background px-8 py-[120px]">
      <div className="mx-auto max-w-[1120px]">
        <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2">
          {/* Terminal */}
          <motion.div
            className="overflow-hidden rounded-2xl bg-foreground shadow-[0_8px_24px_rgba(45,42,38,0.10)]"
            initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, ease: EASE_STANDARD }}
          >
            {/* Title bar */}
            <div className="flex items-center gap-2 border-b border-[rgba(255,255,255,0.06)] px-5 py-3.5">
              <div className="h-3 w-3 rounded-full bg-destructive/60" />
              <div className="h-3 w-3 rounded-full bg-accent/60" />
              <div className="h-3 w-3 rounded-full bg-primary/60" />
              <span className="ml-2 text-[11px] font-medium text-[#9C9689]">masonyt-cli</span>
            </div>

            {/* Code content */}
            <div className="p-6 font-mono text-sm leading-relaxed text-secondary">
              <p>
                <span className="text-accent">const</span>{' '}
                <span className="text-[#6B9BD2]">developer</span>{' '}
                <span className="text-secondary/60">=</span>{' '}
                <span className="text-accent">await</span> masonyt.
                <span className="text-primary">getProfile</span>(me);
              </p>
              <br />
              <p>
                <span className="text-accent">if</span> (developer.ready) {'{'}
              </p>
              <p className="pl-4">
                <span className="text-accent">await</span> masonyt.
                <span className="text-primary">matchProject</span>({'{'}
              </p>
              <p className="pl-8">
                skills: [<span className="text-primary">&quot;React&quot;</span>,{' '}
                <span className="text-primary">&quot;Python&quot;</span>],
              </p>
              <p className="pl-8">
                minRate: <span className="text-primary">&quot;$80/hr&quot;</span>,
              </p>
              <p className="pl-8">
                escrow: <span className="text-primary">true</span>
              </p>
              <p className="pl-4">{'}'});</p>
              <p>{'}'}</p>
              <br />
              <p className="text-[#9C9689]">
                {'// '}
                <span className="text-primary">Matched</span> &middot; 3 projects found
              </p>
              <p className="text-[#9C9689]">{'// '}Escrow funded &middot; Start building</p>
            </div>
          </motion.div>

          {/* Text */}
          <div>
            <motion.div
              className="mb-4 text-xs font-bold uppercase tracking-[2.5px] text-primary"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.65, ease: EASE_STANDARD }}
            >
              Developer-First
            </motion.div>

            <motion.h2
              className="mb-5 text-foreground"
              style={{
                fontFamily: 'Fraunces, Georgia, serif',
                fontSize: '42px',
                fontWeight: 700,
                lineHeight: 1.15,
                letterSpacing: '-0.5px',
              }}
              initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.1 }}
            >
              Tools That Speak
              <br />
              <span className="text-primary">Your Language</span>
            </motion.h2>

            <motion.p
              className="max-w-[420px] text-[17px] leading-[1.65] text-muted-foreground"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.2 }}
            >
              Masonyt is built by developers, for developers. From structured contracts to automated
              frameworks, every tool is designed to let you focus on what you do best: writing
              great code.
            </motion.p>

            <motion.div
              className="mt-8 flex flex-wrap gap-3"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.3 }}
            >
              {['Escrow API', 'Milestone Tracking', 'Code Validation', 'Auto-Contracts'].map(
                (tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-secondary px-3 py-1.5 text-xs font-semibold text-secondary-foreground"
                  >
                    {tag}
                  </span>
                )
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Final CTA Section
// ---------------------------------------------------------------------------

function FinalCTASection() {
  const prefersReducedMotion = useReducedMotion()

  return (
    <section className="relative bg-card px-8 py-[140px] text-center">
      {/* Subtle gradient */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(232,152,90,0.05) 0%, transparent 70%)',
        }}
      />

      <div className="relative z-10">
        <motion.div
          className="mb-4 text-xs font-bold uppercase tracking-[2.5px] text-accent"
          initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.65, ease: EASE_STANDARD }}
        >
          Get Started
        </motion.div>

        <motion.h2
          className="mb-4 text-foreground"
          style={{
            fontFamily: 'Fraunces, Georgia, serif',
            fontSize: 'clamp(36px, 4vw, 48px)',
            fontWeight: 700,
            letterSpacing: '-1px',
          }}
          initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.1 }}
        >
          Ready to Upgrade
          <br />
          Your Career?
        </motion.h2>

        <motion.p
          className="mx-auto mb-10 max-w-[480px] text-lg leading-relaxed text-muted-foreground"
          initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.15 }}
        >
          Join the community of developers building the next generation of software for businesses
          that care. Protected payments, verified reputation, real growth.
        </motion.p>

        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.2 }}
        >
          <LandingButton to="/signup/developer" size="lg">
            Create Developer Account
            <Rocket className="h-5 w-5 transition-transform" />
          </LandingButton>
        </motion.div>

        {/* Agreement illustration */}
        <motion.div
          className="mt-12"
          initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, ease: EASE_STANDARD, delay: 0.3 }}
        >
          <img
            src={ILLUSTRATIONS.agreement}
            alt="Agreement reached"
            className="mx-auto h-auto max-w-[400px]"
            loading="lazy"
          />
        </motion.div>
      </div>
    </section>
  )
}

const navLinks = [
  { href: '#how-it-works', label: 'How It Works' },
  { href: '/', label: 'For Businesses' },
]

export default function DeveloperLanding() {
  return (
    <PublicLayout navLinks={navLinks} ctaText="Join Now" ctaHref="/signup/developer">
      <HeroSection />
      <ProblemSection />
      <BenefitsSection />
      <HowItWorksSection />
      <TerminalSection />
      <FinalCTASection />
    </PublicLayout>
  )
}
